import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Loader2, Users, GraduationCap } from "lucide-react";
import logoSrc from "@/assets/logo.png";

export const Route = createFileRoute("/signup")({
  head: () => ({ meta: [{ title: "Sign Up — Code Cubs Academy" }] }),
  component: SignupPage,
});

const schema = z.object({
  fullName: z.string().trim().min(2).max(80),
  email: z.string().trim().email().max(200),
  password: z.string().min(6).max(100),
  role: z.enum(["parent", "student"]),
});

function SignupPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState<"parent" | "student">("parent");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!loading && user) navigate({ to: "/dashboard" });
  }, [loading, user, navigate]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const parsed = schema.safeParse({
      fullName: String(fd.get("fullName") ?? ""),
      email: String(fd.get("email") ?? ""),
      password: String(fd.get("password") ?? ""),
      role,
    });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      parsed.error.issues.forEach((i) => {
        if (i.path[0]) fieldErrors[i.path[0] as string] = i.message;
      });
      setErrors(fieldErrors);
      return toast.error("Please check the form for errors");
    }
    setErrors({});

    setBusy(true);
    setErrors({});
    try {
      const { data, error } = await supabase.auth.signUp({
        email: parsed.data.email,
        password: parsed.data.password,
        options: {
          data: { full_name: parsed.data.fullName, temp_role: parsed.data.role },
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });
      if (error) {
        toast.error(error.message);
      } else {
        toast.success("تم إنشاء الحساب! راجع بريدك الإلكتروني.");
        navigate({ to: "/dashboard" });
      }
    } catch (err: any) {
      console.error("Signup error:", err);
      toast.error(err.message || "حدث خطأ غير متوقع");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-16 max-w-md">
      <Card className="rounded-3xl border-0 shadow-soft">
        <CardContent className="p-8">
          <div className="flex justify-center mb-6">
            <img src={logoSrc} alt="Code Cubs Academy" className="h-32 w-auto object-contain" />
          </div>
          <h1 className="text-3xl font-extrabold text-center mb-2">Join Code Cubs 🚀</h1>
          <p lang="ar" className="text-center text-muted-foreground mb-6">
            اختر نوع الحساب
          </p>

          <RadioGroup
            value={role}
            onValueChange={(v) => setRole(v as "parent" | "student")}
            className="grid grid-cols-2 gap-3 mb-6"
          >
            <Label
              htmlFor="r-parent"
              className={`flex flex-col items-center justify-center cursor-pointer rounded-2xl border-2 p-4 text-center transition has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring ${role === "parent" ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}
            >
              <RadioGroupItem id="r-parent" value="parent" className="sr-only" />
              <Users className="h-6 w-6 mx-auto mb-2 text-primary" />
              <div lang="ar" className="font-bold text-sm">
                ولي أمر
              </div>
            </Label>
            <Label
              htmlFor="r-student"
              className={`flex flex-col items-center justify-center cursor-pointer rounded-2xl border-2 p-4 text-center transition has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring ${role === "student" ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}
            >
              <RadioGroupItem id="r-student" value="student" className="sr-only" />
              <GraduationCap className="h-6 w-6 mx-auto mb-2 text-primary" />
              <div lang="ar" className="font-bold text-sm">
                طالب
              </div>
            </Label>
          </RadioGroup>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                name="fullName"
                required
                maxLength={80}
                aria-invalid={!!errors.fullName}
              />
              {errors.fullName && (
                <p className="text-[0.8rem] font-medium text-destructive">{errors.fullName}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                maxLength={200}
                aria-invalid={!!errors.email}
              />
              {errors.email && (
                <p className="text-[0.8rem] font-medium text-destructive">{errors.email}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password (6+ chars)</Label>
              <Input
                id="password"
                name="password"
                type="password"
                required
                minLength={6}
                maxLength={100}
                aria-invalid={!!errors.password}
              />
              {errors.password && (
                <p className="text-[0.8rem] font-medium text-destructive">{errors.password}</p>
              )}
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full rounded-full h-12 bg-gradient-primary text-primary-foreground border-0"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
            </Button>
          </form>

          <p lang="ar" className="text-center text-sm mt-6 text-muted-foreground">
            عندك حساب؟{" "}
            <Link to="/login" className="text-primary font-semibold">
              سجل دخول
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
